import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostErinnerungRequest, IPostErinnerungResponse } from 'fa-kt.types';
import { PostErinnerungCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-erinnerung',
  templateUrl: './post-erinnerung.component.html',
  styleUrls: ['./post-erinnerung.component.css'],
})
export class PostErinnerungCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostErinnerungRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostErinnerungRequest> = new EventEmitter<IPostErinnerungRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostErinnerungResponse> = new EventEmitter<IPostErinnerungResponse>();

  /* << fields    */
  /*    fields >> */

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postErinnerung: PostErinnerungCommandService /* << more    */ /*    more >> */,
  ) {
    this.command = sitemap.KOMMUNIKATION.Commands.POST_ERINNERUNG;
    this.form = this.fb.group({
      erinnerung: this.fb.group({
        id: [null, []],
        projekt: [null, []],
        projektName: [null, []],
        kunde: [null, []],
        kundeName: [null, []],
        kommentar: [null, [Validators.required]],
        am: [null, [Validators.required]],
        mitarbeiter: [[], []],
        verfasser: [null, [Validators.required]],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postErinnerung.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostErinnerungRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
    );
    /* << init    */
    this.form.controls.erinnerung.get('mitarbeiter')?.setValidators([Validators.required]);
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostErinnerungRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.erinnerung) {
        value.erinnerung = cleanObject(value.erinnerung);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.erinnerung) {
        payload.erinnerung = cleanObject(payload.erinnerung);
      }
      const response: IPostErinnerungResponse = await this.postErinnerung.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  /* << methods    */
  get canAbmelden() {
    const id = this.auth.$id.getValue();
    const payload = cleanObject(this.form.getRawValue());
    return payload.erinnerung && payload.erinnerung.id && id && payload.erinnerung.mitarbeiter.filter((m: string) => m === id).length > 0;
  }

  async abmelden() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.erinnerung) {
        payload.erinnerung = cleanObject(payload.erinnerung);
      }
      payload.erinnerung.mitarbeiter = payload.erinnerung.mitarbeiter.filter((m: string) => m !== this.auth.$id.getValue());
      const response: IPostErinnerungResponse = await this.postErinnerung.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }
  /*    methods >> */

  get erinnerungId() {
    return this.form.controls.erinnerung.get('id');
  }
  get erinnerungProjekt() {
    return this.form.controls.erinnerung.get('projekt');
  }
  get erinnerungKunde() {
    return this.form.controls.erinnerung.get('kunde');
  }
  get erinnerungKommentar() {
    return this.form.controls.erinnerung.get('kommentar');
  }
  get erinnerungAm() {
    return this.form.controls.erinnerung.get('am');
  }
  get erinnerungMitarbeiter() {
    return this.form.controls.erinnerung.get('mitarbeiter');
  }
  get erinnerungVerfasser() {
    return this.form.controls.erinnerung.get('verfasser');
  }
}
